import Linked from 'shared-components/service/Linked'
import styles from './community.module.css'

export interface Crumb {
  label: string
  /** Absent on the current page (the last crumb), and on a step with nowhere to go (a product's
   *  category when DealHunt sent no taxonomy path for it — `productCategoryTrail`): that one is
   *  named, not linked. */
  href?: string
}

/** One step: a link, the current page, or a step named without a link. */
const CrumbLabel: React.VFC<{ item: Crumb; current: boolean }> = ({ item, current }) => {
  if (item.href) {
    return (
      <Linked href={item.href} className={styles.crumbLink}>
        {item.label}
      </Linked>
    )
  }
  if (current) {
    return (
      <span className={styles.crumbCurrent} aria-current="page">
        {item.label}
      </span>
    )
  }
  return <span className={styles.crumbPlain}>{item.label}</span>
}

/** The community pages' breadcrumb. `components/product/Breadcrumbs` only takes DealHunt
 *  taxonomy items, and these pages are not in the taxonomy. */
const Crumbs: React.VFC<{ items: Crumb[] }> = ({ items }) => (
  <nav className={styles.crumbs} aria-label="Breadcrumb">
    <ol className={styles.crumbList}>
      {items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key -- a fixed trail; a handle may equal a section name
        <li key={`${i}-${item.label}`} className={styles.crumbItem}>
          <CrumbLabel item={item} current={i === items.length - 1} />
          {i < items.length - 1 && (
            <span className={styles.crumbSep} aria-hidden="true">
              {'›'}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
)

/** A link when there is somewhere to go, plain text when there is not (a product DealHunt
 *  could not build a slug for). */
export const MaybeLink: React.FC<
  React.PropsWithChildren<{ href: string | null; className?: string }>
> = ({ href, className, children }) => {
  if (!href) {
    return <span className={className}>{children}</span>
  }
  return (
    <Linked href={href} className={className}>
      {children}
    </Linked>
  )
}

export default Crumbs
